import {
  type ApiClient,
  type ApiResult,
  type CommandIntent,
  type components,
  createIntent,
  isApiError,
  UnknownResultError,
} from '@saay/api-client';
import { useCallback, useRef, useState } from 'react';

import { useApiClient } from './providers.js';

/**
 * The one command hook of the web apps (FTD §14.3, architecture finding A-7): one user intent, one
 * {@link CommandIntent}, one `Idempotency-Key` (AC §7.1).
 *
 * - The same payload keeps the key, so a transport retry or «بررسی وضعیت» after a timeout replays the
 *   stored outcome; an edited payload gets a new key (the intent fingerprints the body).
 * - Success is reported only for a server-confirmed 2xx (no optimistic success, FTD §2.5, §12.3).
 * - **Maker-checker (D-22, AC §4.10, §18.2.3).** A `202` whose body is an `ApprovalPending` is not a
 *   success: nothing was applied. It is its own outcome, `approval-pending`, so a screen shows
 *   «برای تأیید ارسال شد» and never the effect. The check is generic (any command, any action type),
 *   because which commands route to approval is decided by the server's approval policy; a `202`
 *   with another body (for example a status monitor) is an ordinary accepted result.
 * - A timeout or lost connection is `unknown`, never a failure (FTD §2.4): «بررسی وضعیت» resends the
 *   last body with the same key.
 * - After a definitive answer (2xx, including `202 ApprovalPending`, or a non-retryable error) the
 *   intent completes and the next submission is a new intent. A retryable error keeps the key.
 */

/** `202 Accepted` body of a command routed to maker-checker (AC §4.10, §18.2.3), generated. */
export type ApprovalPending = components['schemas']['ApprovalPending'];

/** True when a command answer is `202 ApprovalPending` (AC §4.10): the effect waits for a decider. */
export function isApprovalPending(
  result: ApiResult<unknown>,
): result is ApiResult<ApprovalPending> {
  if (result.status !== 202 || result.data === null || typeof result.data !== 'object') {
    return false;
  }
  const body = result.data as Record<string, unknown>;
  return (
    typeof body.approvalRequestId === 'string' &&
    body.status === 'PENDING' &&
    typeof body.actionType === 'string' &&
    typeof body.expiresAt === 'string'
  );
}

export type CommandStatus =
  'idle' | 'pending' | 'success' | 'approval-pending' | 'error' | 'unknown';

export interface CommandState<T> {
  /** Set while `status` is `approval-pending`. */
  readonly approval: ApprovalPending | undefined;
  /** Set while `status` is `error` or `unknown`. */
  readonly error: unknown;
  /** Set while `status` is `success`. */
  readonly result: ApiResult<T> | undefined;
  readonly status: CommandStatus;
}

export type CommandOutcome<T> =
  | { readonly result: ApiResult<T>; readonly status: 'success' }
  | {
      readonly approval: ApprovalPending;
      readonly result: ApiResult<ApprovalPending>;
      readonly status: 'approval-pending';
    }
  | { readonly error: unknown; readonly status: 'error' }
  | { readonly error: UnknownResultError; readonly status: 'unknown' }
  /** A submission while one is in flight, or a status check before any submission: ignored. */
  | { readonly status: 'busy' };

export interface CommandCallbacks<TInput, T> {
  /** After a server-confirmed `202 ApprovalPending`; a failing follow-up never changes the outcome. */
  readonly onApprovalPending?: (
    approval: ApprovalPending,
    result: ApiResult<ApprovalPending>,
    input: TInput,
  ) => void | Promise<void>;
  /** After a server-confirmed 2xx (not an approval); a failing follow-up never changes the outcome. */
  readonly onSuccess?: (result: ApiResult<T>, input: TInput) => void | Promise<void>;
}

export interface Command<TInput, T> {
  /** Resends the last input with the same key (FTD §11 unknown result). */
  checkStatus(): Promise<CommandOutcome<T>>;
  /** The key of the open intent, if a request was already sent (tests, support). */
  readonly key: string | undefined;
  reset(): void;
  readonly state: CommandState<T>;
  submit(input: TInput): Promise<CommandOutcome<T>>;
}

export type CommandRunner<TInput, T> = (
  client: ApiClient,
  input: TInput,
  intent: CommandIntent,
) => Promise<ApiResult<T>>;

const IDLE: CommandState<never> = {
  approval: undefined,
  error: undefined,
  result: undefined,
  status: 'idle',
};

export function useCommand<TInput, T>(
  name: string,
  run: CommandRunner<TInput, T>,
  callbacks: CommandCallbacks<TInput, T> = {},
): Command<TInput, T> {
  const client = useApiClient();
  const intent = useRef<CommandIntent | null>(null);
  intent.current ??= createIntent(name);
  const lastInput = useRef<{ readonly value: TInput } | null>(null);
  const inFlight = useRef(false);
  // The latest runner and callbacks, so `submit` stays stable while screens re-render.
  const latest = useRef({ callbacks, client, run });
  latest.current = { callbacks, client, run };
  const [state, setState] = useState<CommandState<T>>(IDLE);

  const submit = useCallback(async (input: TInput): Promise<CommandOutcome<T>> => {
    if (inFlight.current) {
      return { status: 'busy' };
    }
    inFlight.current = true;
    lastInput.current = { value: input };
    setState({ ...IDLE, status: 'pending' });
    const current = intent.current!;
    const { callbacks: on, client: api, run: send } = latest.current;
    try {
      const result = await send(api, input, current);
      current.complete();
      if (isApprovalPending(result)) {
        setState({ ...IDLE, approval: result.data, status: 'approval-pending' });
        await Promise.resolve(on.onApprovalPending?.(result.data, result, input)).catch(
          () => undefined,
        );
        return { approval: result.data, result, status: 'approval-pending' };
      }
      setState({ ...IDLE, result, status: 'success' });
      // The server confirmed; a failing follow-up (cache refresh) must not turn it into an error.
      await Promise.resolve(on.onSuccess?.(result, input)).catch(() => undefined);
      return { result, status: 'success' };
    } catch (error) {
      if (error instanceof UnknownResultError) {
        setState({ ...IDLE, error, status: 'unknown' });
        return { error, status: 'unknown' };
      }
      if (!(isApiError(error) && error.retryable)) {
        current.complete();
      }
      setState({ ...IDLE, error, status: 'error' });
      return { error, status: 'error' };
    } finally {
      inFlight.current = false;
    }
  }, []);

  const checkStatus = useCallback(
    (): Promise<CommandOutcome<T>> =>
      lastInput.current === null
        ? Promise.resolve({ status: 'busy' })
        : submit(lastInput.current.value),
    [submit],
  );

  const reset = useCallback(() => setState(IDLE), []);

  return { checkStatus, key: intent.current.key, reset, state, submit };
}

/** The API error code of a command state, if the command failed with an envelope. */
export function commandErrorCode(state: CommandState<unknown>): string | undefined {
  return state.status === 'error' && isApiError(state.error) ? state.error.code : undefined;
}

/** The error of a failed or unknown outcome (`undefined` for success, approval, or busy). */
export function outcomeError(outcome: CommandOutcome<unknown>): unknown {
  return outcome.status === 'error' || outcome.status === 'unknown' ? outcome.error : undefined;
}
